/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Modal, Button, Tabs, Input, Row, Col, message, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Dictionary } from '@onaio/utils/dist/types/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  actionComponentSourcesGet,
  actionPostComponentSettingEdit,
  actionComponentSourceGet,
} from '../../../actions';
import { AkukoAPIService } from '../../../../../services/serviceClass';
import { SOURCES_API } from '../../../../../configs/env';
import { spaceIDSelector } from '../../../../../reducers/selectors/space';
import { userHandleSelector } from '../../../../../reducers/selectors/user';
import { LoadingOutlined, LinkOutlined, DatabaseOutlined } from '@ant-design/icons';
import { ERROR_GENERIC } from '../../../../../configs/constants';
import moment from 'moment';
import { Link } from 'react-router-dom';

const { TabPane } = Tabs;
const { Search } = Input;

export interface SourceInputProps {
  componentIndex: number;
  childIndex?: number;
  childProperty?: string;
  itemIndex?: number;
  item: Dictionary;
}

interface SourceDataType {
  key: string;
  uuid: string;
  name: string;
  space_id: string | null;
  updated: string;
  config?: Dictionary;
  geometries?: Dictionary[];
  cube?: Dictionary;
}

const SourceInput: React.FC<SourceInputProps> = (props: SourceInputProps) => {
  const dispatch = useDispatch();
  const { componentIndex, childIndex, itemIndex, item } = props;
  const { property, label, isMapLayer, parents, disabled } = item;
  const post = useSelector((state: Dictionary) => state.post);
  const [value, setValue] = useState('');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [publicSources, setPublicSources] = useState<Dictionary[]>([]);
  const [accountSources, setAccountSources] = useState<Dictionary[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const spaceId = useSelector(spaceIDSelector);
  const userHandle = useSelector(userHandleSelector);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (parents?.length === 2 && itemIndex !== undefined && childIndex !== undefined) {
      const currentValue =
        post.components[componentIndex]?.[parents[0]]?.[childIndex]?.[parents[1]]?.[itemIndex]?.[
          property
        ];
      setValue(currentValue);
    }
    if (parents?.length === 1 && itemIndex !== undefined) {
      const currentValue = post.components[componentIndex]?.[parents[0]]?.[itemIndex]?.[property];
      setValue(currentValue);
    }
    if (!parents) {
      setValue(post.components[componentIndex][property]);
    }
    if (item.entity === 'post') {
      setValue(post[property]);
    }
  }, [post, parents, itemIndex, childIndex, componentIndex, property, item.entity]);

  useEffect(() => {
    if (modalVisible) {
      const url = spaceId ? `source/space/${spaceId}` : `source/user/account/${userHandle}`;
      const accountService = new AkukoAPIService(SOURCES_API, url);
      const publicService = new AkukoAPIService(SOURCES_API, `source/public`);

      const fetchAccountSources = accountService.list().then((accountSources) => {
        if ((accountSources as Dictionary[])?.length) {
          (accountSources as Dictionary[]).sort(
            (a, b) => (moment(b.updated) as any) - (moment(a.updated) as any)
          );
        }
        setAccountSources(accountSources as Dictionary[]);
        dispatch(actionComponentSourcesGet(accountSources));
      });

      const fetchPublicSources = publicService.list().then((publicSources) => {
        if ((publicSources as Dictionary[])?.length) {
          (publicSources as Dictionary[]).sort(
            (a, b) => (moment(b.updated) as any) - (moment(a.updated) as any)
          );
        }
        setPublicSources(publicSources as Dictionary[]);
        dispatch(actionComponentSourcesGet(publicSources));
      });

      Promise.all([fetchAccountSources, fetchPublicSources])
        .catch((error) => {
          message.error(error?.message || ERROR_GENERIC);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [modalVisible, spaceId, userHandle, dispatch]);

  const handleSelectSource = async (selectedItem: SourceDataType) => {
    setModalVisible(false);
    setIsLoading(true);

    try {
      const service = new AkukoAPIService(SOURCES_API, 'source');
      const res = await service.read(selectedItem.uuid);
      const selectedSource = res as Dictionary;

      dispatch(actionComponentSourceGet(res));

      dispatch(
        actionPostComponentSettingEdit({
          componentIndex: componentIndex,
          parents: parents,
          itemIndex: itemIndex,
          childIndex: childIndex,
          property: property,
          value: selectedItem.uuid,
        })
      );

      if (selectedSource?.cube) {
        dispatch(
          actionPostComponentSettingEdit({
            componentIndex: componentIndex,
            parents: parents,
            itemIndex: itemIndex,
            childIndex: childIndex,
            property: 'cube',
            value: selectedSource.cube,
          })
        );
      }

      if (isMapLayer) {
        dispatch(
          actionPostComponentSettingEdit({
            componentIndex: componentIndex,
            parents: parents,
            itemIndex: itemIndex,
            childIndex: childIndex,
            property: 'geometries',
            value: selectedSource?.geometries ?? [],
          })
        );
        dispatch(
          actionPostComponentSettingEdit({
            componentIndex: componentIndex,
            parents: parents,
            itemIndex: itemIndex,
            childIndex: childIndex,
            property: 'geometryIndex',
            value: 0,
          })
        );
        dispatch(
          actionPostComponentSettingEdit({
            componentIndex: componentIndex,
            parents: parents,
            itemIndex: itemIndex,
            childIndex: childIndex,
            property: 'layerType',
            value: selectedSource?.geometries?.[0]?.type === 'latLng' ? 'circle' : 'fill',
          })
        );
      }
      setValue(selectedItem.uuid);
    } catch (error: any) {
      message.error(error?.message || ERROR_GENERIC);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: ColumnsType<SourceDataType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: SourceDataType) => (
        <Link
          target="_blank"
          to={
            record.space_id === null
              ? `/source/${record.uuid}`
              : `/space/${record.space_id}/sources/${record.uuid}`
          }
          onClick={(e) => e.stopPropagation()}
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }}
          title={text}
        >
          {text}
        </Link>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: 350,
    },
    {
      title: 'Last Modified',
      dataIndex: 'updated',
      key: 'updated',
      render: (text: string) => (
        <span style={{ color: '#777', fontSize: '0.9em' }}>
          {moment(text).fromNow()}
        </span>
      ),
      sorter: (a, b) => moment(a.updated).unix() - moment(b.updated).unix(),
      defaultSortOrder: 'descend',
      width: 150,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record: SourceDataType) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleSelectSource(record);
          }}
        >
          Select Source
        </Button>
      ),
      width: 120,
    },
  ];

  const filterSources = (sources: Dictionary[]): SourceDataType[] => {
    return sources
      .filter(item => item.name?.toLowerCase()?.includes(search.toLowerCase()))
      .filter(item => !isMapLayer || (item.geometries && item.geometries.length > 0))
      .map(item => ({ ...item, key: item.uuid }) as SourceDataType);
  };

  const filteredAccountSources = filterSources(accountSources.filter(item => item.public === false));
  const filteredPublicSources = filterSources(publicSources.filter(item => item.public === true));

  return (
    <Row>
      <Col sm={6}>
        <label>{label}</label>
      </Col>
      <Col sm={18}>
      <Button
        className="source-input-btn"
        // disabled={disabled}
        onClick={() => {
          setModalVisible(true);
        }}
      >
        <span className="source-input-btn-label">
          {post.sources[value]?.name || 'Select Source'}
        </span>
      </Button>
      </Col>
      <Modal
        className="source-select-modal"
        footer={false}
        width={'80%'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        title="Select Source"
      >

        <Tabs defaultActiveKey="private">
          <TabPane tab="My Sources" key="private">
            <div className="source-list-search" style={{ marginBottom: 16 }}>
              <Search
                placeholder="Search My Sources..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                allowClear
              />
            </div>
            <Table
              columns={columns}
              dataSource={filteredAccountSources}
              loading={isLoading}
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ y: 400 }}
            />
          </TabPane>
          <TabPane tab="Public Sources" key="public">
            <div className="source-list-search" style={{ marginBottom: 16 }}>
              <Search
                placeholder="Search Public Sources..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                allowClear
              />
            </div>
            <Table
              columns={columns}
              dataSource={filteredPublicSources}
              loading={isLoading}
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ y: 400 }}
            />
          </TabPane>
        </Tabs>
      </Modal>
    </Row>
  );
};

export { SourceInput };
